import React from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Select,
  FormHelperText,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
  Text,
  Link,
  Box,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ExternalLinkIcon } from '@chakra-ui/icons';

interface TelegramChannelConfigProps {
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

export const TelegramChannelConfig: React.FC<TelegramChannelConfigProps> = ({
  config,
  onChange,
}) => {
  const { isOpen: showToken, onToggle: toggleToken } = useDisclosure();

  const updateConfig = (key: string, value: any) => {
    onChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Bot Configuration */}
      <Box>
        <Text fontSize="sm" color="gray.600" mb={3}>
          To create a Telegram bot, message{' '}
          <Link
            href="https://t.me/botfather"
            color="blue.500"
            isExternal
          >
            @BotFather <ExternalLinkIcon mx="2px" />
          </Link>
          {' '}on Telegram and follow the instructions.
        </Text>
      </Box>

      <FormControl isRequired>
        <FormLabel>Bot Token</FormLabel>
        <InputGroup>
          <Input
            type={showToken ? 'text' : 'password'}
            value={config.botToken || ''}
            onChange={(e) => updateConfig('botToken', e.target.value)}
            placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
            fontFamily="mono"
          />
          <InputRightElement>
            <IconButton
              aria-label={showToken ? 'Hide token' : 'Show token'}
              icon={showToken ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={toggleToken}
            />
          </InputRightElement>
        </InputGroup>
        <FormHelperText>
          The bot token provided by @BotFather
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Bot Username</FormLabel>
        <Input
          value={config.botUsername || ''}
          onChange={(e) => updateConfig('botUsername', e.target.value)}
          placeholder="@your_bot_username"
        />
        <FormHelperText>
          Optional: Your bot's username for reference
        </FormHelperText>
      </FormControl>

      {/* API Settings */}
      <FormControl>
        <FormLabel>API Base URL</FormLabel>
        <Input
          value={config.baseUrl || 'https://api.telegram.org'}
          onChange={(e) => updateConfig('baseUrl', e.target.value)}
          placeholder="https://api.telegram.org"
        />
        <FormHelperText>
          Use custom URL for local bot API server (optional)
        </FormHelperText>
      </FormControl>

      {/* Message Settings */}
      <FormControl>
        <FormLabel>Default Parse Mode</FormLabel>
        <Select
          value={config.parseMode || 'HTML'}
          onChange={(e) => updateConfig('parseMode', e.target.value)}
        >
          <option value="HTML">HTML</option>
          <option value="Markdown">Markdown</option>
          <option value="MarkdownV2">Markdown V2</option>
          <option value="">None</option>
        </Select>
        <FormHelperText>
          Default formatting mode for messages
        </FormHelperText>
      </FormControl>

      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Disable Web Preview</FormLabel>
          <Switch
            isChecked={config.disableWebPagePreview !== false}
            onChange={(e) => updateConfig('disableWebPagePreview', e.target.checked)}
            colorScheme="primary"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Disable Notifications</FormLabel>
          <Switch
            isChecked={config.disableNotification === true}
            onChange={(e) => updateConfig('disableNotification', e.target.checked)}
            colorScheme="primary"
          />
        </FormControl>
      </HStack>

      {/* Rate Limiting */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Rate Limit (msg/sec)</FormLabel>
          <NumberInput
            value={config.rateLimit || 30}
            onChange={(_, value) => updateConfig('rateLimit', value || 30)}
            min={1}
            max={100}
          >
            <NumberInputField />
          </NumberInput>
          <FormHelperText>
            Telegram allows 30 messages per second
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Burst Limit</FormLabel>
          <NumberInput
            value={config.burstLimit || 100}
            onChange={(_, value) => updateConfig('burstLimit', value || 100)}
            min={1}
            max={1000}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Connection Settings */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Timeout (seconds)</FormLabel>
          <NumberInput
            value={config.timeout || 30}
            onChange={(_, value) => updateConfig('timeout', value || 30)}
            min={5}
            max={300}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <FormControl>
          <FormLabel>Max Retries</FormLabel>
          <NumberInput
            value={config.maxRetries || 3}
            onChange={(_, value) => updateConfig('maxRetries', value || 3)}
            min={0}
            max={10}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Admin Settings */}
      <FormControl>
        <FormLabel>Admin Chat IDs</FormLabel>
        <Input
          value={config.adminChatIds?.join(', ') || ''}
          onChange={(e) => {
            const ids = e.target.value
              .split(',')
              .map(id => id.trim())
              .filter(id => id)
              .map(id => parseInt(id))
              .filter(id => !isNaN(id));
            updateConfig('adminChatIds', ids);
          }}
          placeholder="123456789, 987654321"
        />
        <FormHelperText>
          Comma-separated list of admin chat IDs for error reporting
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Error Reporting</FormLabel>
        <Switch
          isChecked={config.errorReporting !== false}
          onChange={(e) => updateConfig('errorReporting', e.target.checked)}
          colorScheme="primary"
        />
      </FormControl>
    </VStack>
  );
};